// ============================================================================
// Beacon → GoHighLevel CRM sync (ONE-WAY, downstream).
//
// IMPORTANT ARCHITECTURE NOTE: Beacon's own Supabase Postgres is the single
// source of truth. GHL is a *mirror* for outreach/pipeline — never authoritative.
// We push a projection of each deal and store the returned GHL ids back on the
// Beacon deal, so Beacon always holds the keys and can reconcile or walk away.
//
// GHL's API v2 custom fields are reliable on CONTACTS (not opportunities), so
// the property data rides on the Contact's custom fields; the Opportunity only
// carries pipeline stage + value + a link back. The token lives server-side in
// the `ghl-sync` Edge Function — the browser never sees it.
// ============================================================================

import type { Deal, Metrics, Stage } from "../types";
import { calc } from "../engine/engine";
import { money0, pct, x2 } from "./format";
import { isCloud, getSupabase } from "./supabase";
import { logUsage } from "./usage";

/** Beacon deal stage → the stage name we create in the "Beacon Deals" pipeline. */
export const GHL_STAGE_MAP: Record<Stage, string> = {
  Prospect: "Prospect",
  Screening: "Screening",
  Underwriting: "Underwriting",
  Offer: "Offer",
  "Under Contract": "Under Contract",
  Closed: "Closed",
};

export interface GhlSyncPayload {
  beaconId: string; // our stable key, written to GHL so we can reconcile
  name: string;
  address: string;
  stageName: string;
  monetaryValue: number; // opportunity value = stabilized value
  status: "open" | "won" | "lost";
  contactId?: string; // when we already know the mirror ids
  opportunityId?: string;
  customFields: Record<string, string | number>;
  note: string;
}

/** Pure: project a deal + its metrics into the CRM payload. Tested. */
export function dealToSyncPayload(deal: Deal, m: Metrics): GhlSyncPayload {
  const g = m.gates;
  return {
    beaconId: deal.id,
    name: deal.name || deal.addr || "Beacon deal",
    address: deal.addr || deal.market,
    stageName: GHL_STAGE_MAP[deal.stage] ?? "Prospect",
    monetaryValue: Math.round(m.stabVal),
    status: deal.stage === "Closed" ? "won" : "open",
    contactId: deal.ghlContactId,
    opportunityId: deal.ghlOpportunityId,
    customFields: {
      beacon_score: m.score.gated ? "GATED" : m.score.composite,
      beacon_band: m.score.band,
      beacon_address: deal.addr || deal.market,
      beacon_market: deal.market,
      beacon_cap_rate: pct(m.capOnCost),
      beacon_dscr: x2(m.dscr),
      beacon_all_in: money0(m.allIn),
      beacon_stab_value: money0(m.stabVal),
      beacon_rent_ceiling: deal.safmr2 ? money0(deal.safmr2) : "n/a",
      beacon_flood_zone: deal.flood,
      beacon_gates: `A ${g.A.t} · B ${g.B.t} · C ${g.C.t} · D ${g.D.t}`,
      beacon_stage: deal.stage,
    },
    note:
      `Beacon underwriting — ${m.score.gated ? "GATED" : m.score.band + " (" + m.score.composite + "/100)"}. ` +
      `Cap ${pct(m.capOnCost)}, DSCR ${x2(m.dscr)}, all-in ${money0(m.allIn)}, stabilized ${money0(m.stabVal)}. ` +
      `Gates: A ${g.A.t}, B ${g.B.t}, C ${g.C.t}, D ${g.D.t}.`,
  };
}

export interface GhlResult {
  configured: boolean; // false when the token/location secret isn't set yet
  contactId?: string;
  opportunityId?: string;
  error?: string;
}

/** Push one deal to GHL (cloud-only relay). Returns the mirror ids to persist. */
export async function syncDealToGhl(deal: Deal): Promise<GhlResult> {
  if (!isCloud()) return { configured: false, error: "CRM sync runs in cloud mode (the GHL token lives in the Edge Function)." };
  const sb = getSupabase()!;
  const payload = dealToSyncPayload(deal, calc(deal));
  const { data, error } = await sb.functions.invoke("ghl-sync", { body: { action: "upsertDeal", payload } });
  if (error) return { configured: true, error: error.message || "GHL sync failed" };
  void logUsage("GoHighLevel", "deal sync");
  return data as GhlResult;
}

/** Verify the token/location are configured and reachable. */
export async function testGhl(): Promise<GhlResult & { locationName?: string }> {
  if (!isCloud()) return { configured: false, error: "Connect in cloud mode." };
  const sb = getSupabase()!;
  const { data, error } = await sb.functions.invoke("ghl-sync", { body: { action: "test" } });
  if (error) return { configured: true, error: error.message || "Could not reach GHL." };
  return data as GhlResult & { locationName?: string };
}
